import { Link } from '@solidjs/router';
import clsx from 'clsx';
import { For, Show, createSignal, onCleanup, onMount, useContext } from 'solid-js';
import { ChevronUpBoldIcon } from '../icons-library/chevron-up-bold';
import { PrimaryMenuData } from '../menu/menu-types';
import urlMatches from '../../tools/url-match';
import removeAmpersand from '../../tools/remove-ampersand';
import { isExternalLink } from '../../tools/check-is-external-url';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { headerNavItemClasses } from './shared';

type HeaderDropdownMenuProps = {
    primaryMenuData: PrimaryMenuData;
};

export const HeaderDropdownMenu = (props: HeaderDropdownMenuProps) => {
    const { localize } = useContext(AppContext);
    const [activeSubmenu, setActiveSubmenu] = createSignal(0);

    const handleOnClick = (_e: Event, ID: number) => {
        setActiveSubmenu(activeSubmenu() === ID ? 0 : ID);
    };

    let navRef: HTMLElement | undefined;
    const handleOutsideClick = () => {
        // Close only when the click/tap lands outside the whole nav. Anchoring to the
        // nav (not the open panel) means tapping a trigger is always "inside", so the
        // toggle can never race this handler and self-close — the bug that made the
        // menu collapse on the same tap on touch devices.
        const handleClickOutside = (event: Event) => {
            if (navRef && !navRef.contains(event.target as Node) && activeSubmenu() !== 0) {
                setActiveSubmenu(0);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && activeSubmenu() !== 0) {
                setActiveSubmenu(0);
            }
        };

        window.addEventListener('click', handleClickOutside);
        window.addEventListener('keydown', handleEscape);

        onCleanup(() => {
            window.removeEventListener('click', handleClickOutside);
            window.removeEventListener('keydown', handleEscape);
        });
    };

    onMount(() => {
        handleOutsideClick();
    });

    const closeSubmenues = () => setActiveSubmenu(0);

    const navItemBase = headerNavItemClasses;

    // Tab-trap keyboard handling for the open submenu.
    // - Tab from last dropdown item: close + focus the next top-level nav item.
    // - Shift+Tab from first dropdown item: close + return focus to the trigger (so the next
    //   Shift+Tab falls out of this item naturally to the previous top-level nav item).
    const handleWrapperKeydown = (event: KeyboardEvent, itemID: number) => {
        if (event.key !== 'Tab' || activeSubmenu() !== itemID) {
            return;
        }

        const wrapper = event.currentTarget as HTMLElement;
        const trigger = wrapper.querySelector<HTMLButtonElement>(':scope > button');
        const submenuLinks = wrapper.querySelectorAll<HTMLElement>('.sub-menu a[href]');
        if (!trigger || submenuLinks.length === 0) {
            return;
        }

        const first = submenuLinks[0];
        const last = submenuLinks[submenuLinks.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && active === first) {
            event.preventDefault();
            setActiveSubmenu(0);
            trigger.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            setActiveSubmenu(0);
            let next: Element | null = wrapper.nextElementSibling;
            while (next) {
                const candidate = next instanceof HTMLElement && next.matches('a[href], button') ? next : next.querySelector<HTMLElement>('a[href], button');
                if (candidate) {
                    candidate.focus();
                    return;
                }
                next = next.nextElementSibling;
            }
        }
    };

    return (
        <nav class="flex flex-wrap gap-x-cvi-9" ref={navRef}>
            <For each={props.primaryMenuData?.menu}>
                {(item, index) => (
                    <Show
                        fallback={
                            /* Top-level link without children — DS `secondary navigation/Item`. */
                            <Link
                                href={item.url}
                                class={clsx(
                                    navItemBase,
                                    'flex items-center py-cvi-5 cursor-pointer',
                                    urlMatches(item.url) ? 'font-cvi-bold' : 'font-cvi-medium'
                                )}
                                {...(isExternalLink(item.url)
                                    ? {
                                        target: '_blank',
                                        rel: 'noopener noreferrer',
                                    } : {})}
                                onClick={closeSubmenues}
                            >
                                {removeAmpersand(item.title)}
                            </Link>
                        }
                        when={item.children}
                    >
                        <div class="relative" onKeyDown={(e) => handleWrapperKeydown(e, item.ID)}>
                            <button
                                aria-label={activeSubmenu() === item.ID ? localize('close-submenu', 'Close submenu') : localize('open-submenu', 'Open submenu')}
                                data-index={index()}
                                onClick={(e) => handleOnClick(e, item.ID)}
                                class={clsx(
                                    navItemBase,
                                    'flex items-center gap-cvi-3 py-cvi-5 cursor-pointer',
                                    /* Bold only when the parent's OWN page is active. A child page being the
                                       current page gets no indicator on the parent. */
                                    item.url && urlMatches(item.url) ? 'font-cvi-bold' : 'font-cvi-medium'
                                )}
                            >
                                {removeAmpersand(item.title)}
                                <span
                                    class={clsx('inline-flex items-center transition-transform duration-200 origin-center', {
                                        'rotate-180': activeSubmenu() === item.ID,
                                    })}
                                >
                                    <ChevronUpBoldIcon ariaLabel={localize('toggle-submenu', 'Toggle submenu')} />
                                </span>
                            </button>

                            <Show when={item.ID === activeSubmenu()}>
                                <div class="sub-menu absolute animate-fade-in-move-up mt-[0.5rem] z-[1]">
                                    <ul class="flex flex-col gap-cvi-1 min-w-[25rem] translate-x-5 bg-cvi-bg-elevation-raised rounded-cvi-md shadow-elevation-raised pt-cvi-6 pb-cvi-4 px-cvi-4 text-left [&>li>a::first-letter]:uppercase">
                                        <Show when={item.url}>
                                            <li>
                                                <Link
                                                    class={clsx(
                                                        'block px-cvi-6 py-cvi-4 rounded-cvi-sm label-md transition-colors duration-200 ease-in-out',
                                                        'hover:bg-cvi-bg-menu-hover hover:text-cvi-fg-default',
                                                        'focus-visible:text-cvi-fg-link active:text-cvi-fg-link',
                                                        urlMatches(item.url)
                                                            ? 'bg-cvi-bg-menu-active text-cvi-fg-default font-cvi-bold'
                                                            : 'text-cvi-fg-muted font-cvi-medium'
                                                    )}
                                                    href={item.url}
                                                    {...(isExternalLink(item.url)
                                                        ? {
                                                            target: '_blank',
                                                            rel: 'noopener noreferrer',
                                                        }
                                                        : {})}
                                                    onClick={closeSubmenues}
                                                >
                                                    {removeAmpersand(item.title)}
                                                </Link>
                                            </li>
                                        </Show>
                                        <For each={item?.children}>
                                            {(subItem: any) => (
                                                <li>
                                                    <Link
                                                        class={clsx(
                                                            'block px-cvi-6 py-cvi-4 rounded-cvi-sm label-md transition-colors duration-200 ease-in-out',
                                                            'hover:bg-cvi-bg-menu-hover hover:text-cvi-fg-default',
                                                            'focus-visible:text-cvi-fg-link active:text-cvi-fg-link',
                                                            urlMatches(subItem.url)
                                                                ? 'bg-cvi-bg-menu-active text-cvi-fg-default font-cvi-bold'
                                                                : 'text-cvi-fg-muted font-cvi-medium'
                                                        )}
                                                        href={subItem.url}
                                                        {...(isExternalLink(subItem.url)
                                                            ? {
                                                                target: '_blank',
                                                                rel: 'noopener noreferrer',
                                                            }
                                                            : {})}
                                                        onClick={closeSubmenues}
                                                    >
                                                        {removeAmpersand(subItem.title)}
                                                    </Link>
                                                </li>
                                            )}
                                        </For>
                                    </ul>
                                </div>
                            </Show>
                        </div>
                    </Show>
                )}
            </For>
        </nav>
    );
};
